import { useEffect, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import {
  useGetApiKeyInfoQuery,
  useGenerateApiKeyMutation,
  useDeleteApiKeyMutation
} from '@/app/common/api/apiKeyApi';
import {
  Alert,
  Button,
  Card,
  Col,
  Row,
  Spinner,
  Form,
  OverlayTrigger,
  Tooltip
} from 'react-bootstrap';
import { errorMessage, successMessage } from '@/utils/toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import GenerateApiKeyModal from '../components/api-key/GenerateApiKeyModal';
import DeleteApiKeyModal from '../components/api-key/DeleteApiKeyModal';
import { useCheckAccessQuery } from '@/app/common/api/accessLevelApi';
import useDomain from '@/hooks/useDomain';
import IconAlert from '@/components/common/IconAlert';
import TextLoading from '@/app/common/components/skeleton/TextLoading';
import type { SettingsOutletContext } from '../index';

type ApiErrorShape = {
  data?: { msg?: string; message?: string };
};

type ApiKeyDateParts = {
  Date?: string;
  Time?: string;
};

type GenerateApiKeyData = {
  api_key?: string;
};

const getApiErrorMessage = (error: unknown, fallback: string): string => {
  const err = error as ApiErrorShape;
  return err?.data?.msg || err?.data?.message || fallback;
};

const ApiKeySettings = () => {
  const [, setBreadcrumbData] = useOutletContext<SettingsOutletContext>();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState<string | null>(null);
  const { domain_slug } = useDomain();
  const { data, isFetching: isFetchingAi } = useCheckAccessQuery({
    domain_slug,
    tool_name: 'ai'
  });
  const {
    data: apiKeyInfo,
    isLoading,
    isFetching,
    refetch
  } = useGetApiKeyInfoQuery();

  const [generateApiKey, { isLoading: isGenerating }] =
    useGenerateApiKeyMutation();

  const [deleteApiKey, { isLoading: isDeleting }] = useDeleteApiKeyMutation();

  useEffect(() => {
    setBreadcrumbData([{ text: 'تنظیمات API Key', href: '#' }]);
  }, [setBreadcrumbData]);

  const apiKeyData = apiKeyInfo?.data;

  const hasApiKey =
    apiKeyData?.api_key_preview !== null ||
    apiKeyData?.api_key_created_at !== null ||
    apiKeyData?.api_key_last_used_at !== null;

  const handleGenerate = async () => {
    try {
      const result = await generateApiKey().unwrap();

      if (result.code === 200 && result.success) {
        successMessage('API Key با موفقیت ایجاد شد');
        const keyData = result.data as GenerateApiKeyData | undefined;
        setNewlyCreatedKey(keyData?.api_key ?? null);
        setShowCreateModal(false);
        setShowRegenerateModal(false);
        refetch();
      } else {
        errorMessage(result.msg || 'خطا در ایجاد API Key');
      }
    } catch (error) {
      errorMessage(
        getApiErrorMessage(
          error,
          'خطا در ایجاد API Key. لطفا دوباره تلاش کنید.'
        )
      );
    }
  };

  const handleDelete = async () => {
    try {
      const result = await deleteApiKey().unwrap();

      if (result.code === 200 && result.success) {
        successMessage('API Key با موفقیت حذف شد');
        setShowDeleteModal(false);
        refetch();
      } else {
        errorMessage(result.msg || 'خطا در حذف API Key');
      }
    } catch (error) {
      errorMessage(
        getApiErrorMessage(
          error,
          'خطا در حذف API Key. لطفا دوباره تلاش کنید.'
        )
      );
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      successMessage('API Key در کلیپ‌بورد کپی شد');
    });
  };

  const formatDate = (
    dateObj: ApiKeyDateParts | string | null | undefined
  ): string => {
    if (!dateObj) return '-';
    if (typeof dateObj === 'string') return dateObj;
    return `${dateObj.Date ?? ''} ${dateObj.Time || ''}`.trim() || '-';
  };

  return (
    <Row className="g-0 justify-content-center">
      <Col lg={10} xl={8}>
        <Card className="mb-3">
          <Card.Header className="border-bottom text-800">
            <h5 className="mb-0">مدیریت API Key</h5>
          </Card.Header>
          {isFetchingAi && (
            <Card.Body className="text-center">
              <TextLoading height={140} />
              <div className="mt-3">
                <TextLoading width={50} height={70} />
              </div>
            </Card.Body>
          )}
          {data?.data?.have_access === false && (
            <Card.Body>
              <IconAlert variant="warning">
                {
                  'API key ها تنها برای طرح های پیشرفته ۱ یا بالاتر در دسترس هستند. برای ارتقا میتوانید به صفحه'
                }
                &nbsp;
                <Link to={'/prorogate-or-upgrade/'}>خرید طرح</Link>
                &nbsp;مراجعه کنید.
              </IconAlert>
            </Card.Body>
          )}
          {data?.data?.have_access === true && (
            <>
              <Card.Body>
                <Alert variant="info" className="mb-2">
                  <h6 className="alert-heading">راهنمای استفاده از API Key</h6>
                  <ul className="mb-0 ps-3 fs--1">
                    <li>
                      API Key شما برای دسترسی به API های پنل استفاده می‌شود
                    </li>
                    <li>
                      هر API Key را در یک مکان امن نگهداری کنید و آن را با کسی
                      به اشتراک نگذارید
                    </li>
                    <li>
                      API Key فقط یکبار ایجاد می‌شود. پس از ایجاد، آن را در جای
                      امنی ذخیره کنید
                    </li>
                    <li>
                      برای استفاده از API، API Key را در header درخواست‌های خود
                      با نام <code>X-API-Key</code> ارسال کنید
                    </li>
                    <li>
                      برای مشاهده مستندات کامل API، به{' '}
                      <a
                        href="https://mizfa.tools/docs/api/#description/introduction"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-decoration-none"
                      >
                        مستندات API
                      </a>{' '}
                      مراجعه کنید
                    </li>
                  </ul>
                  <div className="mt-3">
                    <Button
                      variant="outline-info"
                      size="sm"
                      href="https://mizfa.tools/docs/api/#description/introduction"
                      target="_blank"
                      rel="noopener noreferrer"
                      as="a"
                    >
                      <FontAwesomeIcon icon="book" className="me-1" />
                      مشاهده مستندات API
                    </Button>
                  </div>
                </Alert>

                {newlyCreatedKey && (
                  <Alert variant="success" className="mb-4">
                    <h6 className="alert-heading">API Key جدید ایجاد شد!</h6>
                    <p className="mb-2 fs--1">
                      لطفا این کلید را کپی کنید. این کلید دیگر نمایش داده نخواهد
                      شد.
                    </p>
                    <div className="d-flex gap-2 align-items-center">
                      <Form.Control
                        type="text"
                        className="fs--1"
                        value={newlyCreatedKey}
                        readOnly
                      />
                      <Button
                        size="sm"
                        onClick={() => copyToClipboard(newlyCreatedKey)}
                      >
                        کپی
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setNewlyCreatedKey(null)}
                      >
                        بستن
                      </Button>
                    </div>
                  </Alert>
                )}

                {isLoading || isFetching ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                  </div>
                ) : !hasApiKey ? (
                  <div className="text-center py-4">
                    <p className="text-muted mb-3">
                      هنوز هیچ API Key ایجاد نشده است.
                    </p>
                    <Button
                      variant="falcon-primary"
                      onClick={() => setShowCreateModal(true)}
                    >
                      ایجاد API Key
                    </Button>
                  </div>
                ) : (
                  <div>
                    <Row>
                      <Col md={12}>
                        <div className="mb-3">
                          <label className="form-label">
                            پیش نمایش API Key{' '}
                          </label>
                          <div className="d-flex align-items-center gap-2">
                            <Form.Control
                              type="text"
                              value={apiKeyData?.api_key_preview ?? ''}
                              readOnly
                            />
                          </div>
                        </div>
                      </Col>
                      <Col md={12}>
                        <label className="form-label">
                          تاریخ ایجاد:{' '}
                          {formatDate(
                            apiKeyData?.api_key_created_at as
                              | ApiKeyDateParts
                              | string
                              | null
                              | undefined
                          )}
                        </label>
                      </Col>
                      {apiKeyData?.api_key_last_used_at && (
                        <Col md={12}>
                          <label className="form-label">
                            آخرین استفاده:{' '}
                            {formatDate(
                              apiKeyData.api_key_last_used_at as
                                | ApiKeyDateParts
                                | string
                            )}
                          </label>
                        </Col>
                      )}
                    </Row>
                  </div>
                )}
              </Card.Body>
              {hasApiKey && (
                <Card.Footer className="border-top">
                  <div className="d-flex gap-2 justify-content-end">
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip>حذف API Key</Tooltip>}
                    >
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => setShowDeleteModal(true)}
                      >
                        <FontAwesomeIcon icon="trash" />
                      </Button>
                    </OverlayTrigger>
                    <Button
                      variant="falcon-primary"
                      size="sm"
                      onClick={() => setShowRegenerateModal(true)}
                    >
                      <FontAwesomeIcon icon="redo" className="me-1" />
                      ساخت مجدد
                    </Button>
                  </div>
                </Card.Footer>
              )}
            </>
          )}
        </Card>

        <GenerateApiKeyModal
          show={showCreateModal}
          onHide={() => setShowCreateModal(false)}
          onConfirm={handleGenerate}
          isGenerating={isGenerating}
          type="create"
        />

        <DeleteApiKeyModal
          show={showDeleteModal}
          onHide={() => setShowDeleteModal(false)}
          onConfirm={handleDelete}
          isDeleting={isDeleting}
        />

        <GenerateApiKeyModal
          show={showRegenerateModal}
          onHide={() => setShowRegenerateModal(false)}
          onConfirm={handleGenerate}
          isGenerating={isGenerating}
          type="regenerate"
        />
      </Col>
    </Row>
  );
};

export default ApiKeySettings;
